import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, ArrowLeft, Check } from "lucide-react";
import { api, formatMoney } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { SizeGuide } from "@/components/SizeGuide";
import { Reviews } from "@/components/Reviews";
import { fbTrack } from "@/lib/pixel";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);
  const [design, setDesign] = useState(null);
  const [printName, setPrintName] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setSize(null);
      setDesign(null);
      setActiveImg(0);
    }).catch(() => navigate("/shop"));
  }, [id, navigate]);

  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} | Go Big Forever`;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: product.image,
      description: product.description,
      sku: product.id,
      brand: { "@type": "Brand", name: "Go Big Forever" },
      offers: {
        "@type": "Offer",
        url: window.location.href,
        price: product.price,
        priceCurrency: "NZD",
        availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      },
    });
    document.head.appendChild(script);
    fbTrack("ViewContent", {
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      value: product.price,
      currency: "NZD",
    });
    return () => {
      document.title = "Fishing & Hunting Shirts and Hoodies NZ & Australia | Go Big Forever";
      script.remove();
    };
  }, [product]);

  if (!product)
    return <div className="min-h-[60vh] flex items-center justify-center font-display">Loading…</div>;

  const hasSizes = Array.isArray(product.sizes) && product.sizes.length > 0;
  const hasDesigns = Array.isArray(product.designs) && product.designs.length > 0;
  const gallery = [product.image, ...(product.images || [])].filter((u, i, a) => u && a.indexOf(u) === i);

  const addToCart = () => {
    if (hasSizes && !size) {
      toast.error("Please select a size first.");
      return;
    }
    if (hasDesigns && !design) {
      toast.error("Please pick a design first.");
      return;
    }
    addItem(product, qty, size, design, product.allows_personalization ? printName.trim() || null : null);
    fbTrack("AddToCart", {
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      value: product.price * qty,
      currency: "NZD",
    });
    toast.success(`${qty} × ${product.name}${size ? ` (${size})` : ""}${design ? ` · ${design}` : ""} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm font-bold mb-8 hover:text-brand" data-testid="back-to-shop">
        <ArrowLeft size={16} /> Back to shop
      </Link>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div className="border border-ink/10 bg-secondary">
          <div className="relative">
            <img src={gallery[activeImg] || product.image} alt={product.name} className="w-full aspect-square object-cover" data-testid="product-image" />
            {product.allows_personalization && printName.trim() && (
              <>
                <svg viewBox="0 0 400 110" className="absolute left-0 right-0 top-[16%] w-full pointer-events-none" data-testid="name-preview-overlay" aria-hidden="true">
                  <defs>
                    <path id="name-arc" d="M 40 95 Q 200 25 360 95" fill="none" />
                  </defs>
                  <text
                    fill="#ffffff"
                    stroke="rgba(0,0,0,0.55)"
                    strokeWidth="1.2"
                    paintOrder="stroke"
                    style={{
                      fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
                      fontWeight: 900,
                      letterSpacing: "0.12em",
                      fontSize: printName.trim().length > 12 ? 26 : printName.trim().length > 7 ? 34 : 42,
                      textTransform: "uppercase",
                      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.45))",
                    }}
                  >
                    <textPath href="#name-arc" startOffset="50%" textAnchor="middle">
                      {printName.trim().toUpperCase()}
                    </textPath>
                  </text>
                </svg>
                <span className="absolute bottom-2 left-2 bg-ink/70 text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded" data-testid="name-preview-note">
                  Print preview — position may vary
                </span>
              </>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap" data-testid="product-gallery">
              {gallery.map((u, i) => (
                <button key={u} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                  className={`h-16 w-16 rounded-md overflow-hidden border-2 transition-colors ${i === activeImg ? "border-brand" : "border-ink/10 hover:border-ink/40"}`}>
                  <img src={u} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="overline mb-3">{product.category}</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-4" data-testid="product-title">
            {product.name}
          </h1>
          <p className="font-mono text-3xl font-bold mb-6" data-testid="product-price">{formatMoney(product.price)}</p>
          <p className="text-muted-foreground leading-relaxed mb-8">{product.description}</p>

          {hasDesigns && (
            <div className="mb-8">
              <p className="overline mb-3">Pick your design</p>
              <div className="flex flex-wrap gap-3" data-testid="design-selector">
                {product.designs.map((d, i) => (
                  <button
                    key={d}
                    onClick={() => { setDesign(d); if (gallery.length > i) setActiveImg(i); }}
                    data-testid={`design-btn-${i}`}
                    className={`flex flex-col items-center gap-1.5 p-1.5 border-2 rounded-md transition-colors ${design === d ? "border-brand" : "border-ink/15 hover:border-ink/50"}`}
                  >
                    {gallery[i] && <img src={gallery[i]} alt={d} className="h-16 w-16 object-cover rounded" />}
                    <span className={`text-xs font-medium max-w-[76px] truncate ${design === d ? "text-brand" : ""}`}>{d}</span>
                  </button>
                ))}
              </div>
              {!design && <p className="text-xs text-muted-foreground mt-2">Tap a design to select it</p>}
            </div>
          )}

          {product.allows_personalization && (
            <div className="mb-8">
              <p className="overline mb-3">Add a name (optional)</p>
              <input
                value={printName}
                onChange={(e) => setPrintName(e.target.value.slice(0, 20))}
                placeholder="Name to print on the shirt"
                data-testid="personalization-input"
                className="w-full max-w-xs border border-ink/20 focus:border-brand outline-none rounded-md p-3 text-base"
              />
              <p className="text-xs text-muted-foreground mt-1">{printName.length}/20 characters{printName.trim() ? " — see the preview on the photo" : ""}</p>
            </div>
          )}

          {hasSizes && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <p className="overline">Select size</p>
                <SizeGuide sizes={product.sizes} chart={product.size_chart} />
              </div>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`min-w-[3rem] px-4 py-3 text-sm font-bold border transition-colors ${
                      size === s ? "bg-ink text-white border-ink" : "border-ink/20 hover:border-ink"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2 mb-8 text-sm">
            <p className="flex items-center gap-2"><Check size={16} className="text-brand" /> In stock — ships worldwide</p>
            <p className="flex items-center gap-2"><Check size={16} className="text-brand" /> Tax calculated at checkout by country</p>
            <p className="flex items-center gap-2"><Check size={16} className="text-brand" /> Free NZ & AU shipping on orders over $75</p>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-ink">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" className="px-4 py-3 hover:bg-secondary">
                <Minus size={16} />
              </button>
              <span className="px-6 font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase" className="px-4 py-3 hover:bg-secondary">
                <Plus size={16} />
              </button>
            </div>
            <button onClick={addToCart} className="btn-primary flex-1" data-testid="add-to-cart-detail">
              <ShoppingBag size={18} /> Add to cart
            </button>
          </div>
        </div>
      </div>

      <Reviews productId={product.id} />
    </div>
  );
}
